import React, { useCallback, FC, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'react-bootstrap-icons';
import { Paper, Button } from '@mui/material';

import { reduxActions, useDispatch } from 'store';
import { useAppNavigate } from 'hooks';
import { Link } from 'components/controls';
import { WiseAvatar } from 'components/wise';
import {
  DashboardIcon,
  SalesIcon,
  ClientsIcon,
  OfferIcon,
  ClientsCartsIcon,
  CalendarPlusIcon
} from 'assets/icons';

import styles from 'theme/components/containers/TraderDashboardMenu/TraderDashboardMenu.module.scss';

interface IMenuItem {
  label: string;
  icon?: ReactElement;
  items?: { label: string; path: string }[];
  path?: string;
}

export interface ITraderDashboardMenuProps {
  isCondensed?: boolean;
  onToggleCondensed?: () => void;
}

const TraderDashboardMenu: FC<ITraderDashboardMenuProps> = ({
  isCondensed = false,
  onToggleCondensed
}) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  // ścieżka z url
  const { pathname } = useLocation();

  // config menu
  const menuItems: IMenuItem[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/trader'
    },
    {
      label: 'Sprzedaż',
      icon: <SalesIcon />,
      path: '/trader/sales'
    },
    {
      label: 'Klienci',
      icon: <ClientsIcon />,
      path: '/trader/clients'
    },
    {
      label: 'Oferta',
      icon: <OfferIcon />,
      path: '/trader/offers'
    },
    {
      label: 'Zadania',
      icon: <CalendarPlusIcon />,
      path: '/trader/tasks'
    },
    {
      label: 'Koszyki klientów',
      icon: <ClientsCartsIcon />,
      path: '/trader/client-carts'
    },
    // {
    //   label: 'Terminarz',
    //   icon: <CalendarPlusIcon />,
    //   path: '/trader/calendar'
    // }
  ];

  // funkcja testująca czy podana ścieżka jest aktualną stroną
  const isCurrentPath = useCallback(
    (testedPath?: string) => {
      if (!testedPath) return false;
      // Dla /trader sprawdzamy czy pathname zaczyna się od /trader i nie ma dalszych segmentów (lub jest dokładnie /trader)
      if (testedPath === '/trader') {
        return pathname === '/trader' || pathname.match(/^\/[^/]+\/trader$/);
      }
      // Dla innych ścieżek używamy standardowego regex
      return new RegExp(`^/[^\\/]+${testedPath}$`).test(pathname);
    },
    [pathname]
  );

  const renderItems = (parentItem: IMenuItem, index: number) => {
    if (parentItem.path && !parentItem.items) {
      const isActive = isCurrentPath(parentItem.path);
      return (
        <div className={styles.menuItem} key={index}>
          <Link
            to={parentItem.path}
            onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
            className={classnames(styles.link, {
              [styles.active]: isActive,
              [styles.condensed]: isCondensed
            })}>
            <div className={styles.iconWrapper}>{parentItem.icon}</div>
            {!isCondensed && t(parentItem.label)}
          </Link>
        </div>
      );
    }

    return null;
  };

  return (
    <Paper
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderDashboardMenu',
        { [styles.condensed]: isCondensed }
      )}>
      <div className={styles.menuItems}>
        {menuItems.map((item, index) => renderItems(item, index))}
      </div>
      <div className={classnames(styles.bottomSection, {[styles.bottomSectionCondensed]: isCondensed})}>
        <WiseAvatar
          withDetails={!isCondensed}
          fullWidth
          onClick={() => {
            dispatch(reduxActions.setIsMobileMenu(false));
            navigate('/trader');
          }}
          className={styles.userCard}
        />
        {onToggleCondensed && (
          <Button
            variant="text"
            className={styles.collapseButton}
            startIcon={isCondensed ? <ArrowRight /> : <ArrowLeft />}
            onClick={onToggleCondensed}>
            {isCondensed ? '' : t('Zwiń panel')}
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default TraderDashboardMenu;
